import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import useSWR from 'swr'

/**
 * Lazily fetch a sentinel's target/driver tag ids. They live only in the
 * entry's result.json (the entry itself carries just counts), so this
 * downloads the file via a signed URL — pass a null entryId until the caller
 * actually needs the answer (e.g. a row is expanded).
 */
export const useSentinelDrivers = (
  entryId: string | null,
  resultPath: string | undefined
) => {
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()

  const { data, error, isLoading } = useSWR(
    isAuthenticated && entryId && resultPath
      ? ['sentinel-drivers', entryId]
      : null,
    async () => {
      const res = await authorizedAxios.get(
        `${ROLODEX_URL}/api/v2/files/${encodeURIComponent(entryId ?? '')}/${encodeURIComponent(resultPath ?? '')}`
      )
      const download = await fetch(res.data as string)
      if (!download.ok) {
        throw new Error(`result file download failed (HTTP ${download.status})`)
      }
      const file = (await download.json()) as {
        target?: string
        drivers?: string[]
        models?: {
          methods?: Record<
            string,
            {
              band_k?: number
              per_tag?: Record<
                string,
                {
                  score_mae_scaled?: number | null
                  drift?: number | null
                  score_std?: number | null
                  err_scale?: number | null
                  train_rows?: number
                  score_rows?: number
                }
              >
            }
          >
        }
      }
      // Holdout quality stats per method, keyed for the target tag — the
      // closest thing the pipeline has to an "error rate" per model.
      const methodStats: Record<string, SentinelMethodStats> = {}
      const target = file.target
      if (target && file.models?.methods) {
        for (const [method, result] of Object.entries(file.models.methods)) {
          const perTag = result.per_tag?.[target]
          if (!perTag) continue
          methodStats[method] = {
            scaledMae: perTag.score_mae_scaled ?? null,
            drift: perTag.drift ?? null,
            errScale: perTag.err_scale ?? null,
            scoreRows: perTag.score_rows ?? null,
          }
        }
      }
      return { target: file.target, drivers: file.drivers ?? [], methodStats }
    },
    { revalidateOnFocus: false }
  )

  return {
    target: data?.target,
    drivers: data?.drivers,
    methodStats: data?.methodStats,
    error,
    isLoading,
  }
}

/** Holdout quality stats for one model method on the target tag. */
export interface SentinelMethodStats {
  /** Scaled MAE on the holdout window — lower is better. */
  scaledMae: number | null
  drift: number | null
  errScale: number | null
  scoreRows: number | null
}
